import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Instagram, Linkedin, Music2, Twitter, Mail, Phone, MapPin } from "lucide-react";

export const Footer = ({ onBookNow }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end end"] });
  const wordmarkScale = useTransform(scrollYProgress, [0, 1], [0.92, 1]);
  const wordmarkRotateX = useTransform(scrollYProgress, [0, 1], [10, 0]);

  return (
    <footer
      id="site-footer"
      ref={ref}
      data-testid="footer-section"
      className="relative pt-24 pb-10 perspective-scene overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,#120202,transparent_70%)]" />
      <div
        aria-hidden
        className="absolute -top-20 -left-40 h-[420px] w-[420px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #4ea8ff, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-20 -right-40 h-[460px] w-[460px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #ff3d8e, transparent 70%)" }}
      />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E10600]/50 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-6">
          <div className="md:col-span-5">
            <p className="mono-eyebrow mb-4">Let's make something</p>
            <h3 className="font-display text-3xl md:text-5xl text-white leading-[1.05] tracking-tight">
              Ready to shoot your next <span className="text-[#E10600]">story</span>?
            </h3>
            <p className="mt-4 text-neutral-400 font-body max-w-md">
              Book a shoot, or just say hi. We reply within a few hours on weekdays.
            </p>

            <button
              onClick={onBookNow}
              data-testid="footer-book-now-button"
              className="btn-3d mt-7 group inline-flex items-center gap-2 rounded-full bg-[#E10600] px-7 py-3.5 text-sm font-medium text-white"
            >
              <span className="relative z-10">Book a Shoot</span>
              <ArrowUpRight className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>

            <div className="mt-10 space-y-3 text-sm font-body">
              <a
                href="mailto:blinkshotsusa@gmail.com"
                data-testid="footer-email"
                className="flex items-center gap-3 text-neutral-300 hover:text-white"
              >
                <Mail className="h-4 w-4 text-[#E10600]" />
                blinkshotsusa@gmail.com
              </a>
              <a
                href="tel:+15516897544"
                data-testid="footer-phone"
                className="flex items-center gap-3 text-neutral-300 hover:text-white"
              >
                <Phone className="h-4 w-4 text-[#E10600]" />
                +1 (551) 689-7544
              </a>
              <p className="flex items-center gap-3 text-neutral-300" data-testid="footer-location">
                <MapPin className="h-4 w-4 text-[#E10600]" />
                NYC · 1st US-based content creation brand
              </p>
            </div>
          </div>

          <div className="md:col-span-4 grid grid-cols-2 gap-8">
            <div>
              <p className="mono-eyebrow mb-3">Navigate</p>
              <ul className="space-y-2">
                {[
                  { h: "#about", l: "About" },
                  { h: "#pricing", l: "Pricing" },
                  { h: "#work", l: "Our Work" },
                  { h: "#join", l: "Join" },
                ].map((i) => (
                  <li key={i.h}>
                    <a
                      href={i.h}
                      data-testid={`footer-nav-${i.l.toLowerCase()}`}
                      className="text-neutral-300 hover:text-white"
                    >
                      {i.l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="mono-eyebrow mb-3">Social</p>
              <ul className="space-y-2">
                <li>
                  <a
                    href="https://instagram.com/blinkshotsusa"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-social-instagram"
                    className="flex items-center gap-2 text-neutral-300 hover:text-white"
                  >
                    <Instagram className="h-4 w-4" /> @blinkshotsusa
                  </a>
                </li>
                <li>
                  <a
                    href="https://tiktok.com/@blinkshotsusa"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-social-tiktok"
                    className="flex items-center gap-2 text-neutral-300 hover:text-white"
                  >
                    <Music2 className="h-4 w-4" /> @blinkshotsusa
                  </a>
                </li>
                <li>
                  <a
                    href="https://twitter.com/blinkshotsusa"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-social-twitter"
                    className="flex items-center gap-2 text-neutral-300 hover:text-white"
                  >
                    <Twitter className="h-4 w-4" /> @blinkshotsusa
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.linkedin.com/in/blink-shots-b8415a392/?skipRedirect=true"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-social-linkedin"
                    className="flex items-center gap-2 text-neutral-300 hover:text-white"
                  >
                    <Linkedin className="h-4 w-4" /> Blinkshots
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="mono-eyebrow mb-3">Studio</p>
            <p className="text-neutral-400 font-body text-sm">
              BlinkShots is the 1st US-based content creation brand — headquartered in NYC,
              crafting premium short-form visuals on the latest iPhone.
            </p>
            <div className="mt-5 rounded-2xl glass p-4">
              <p className="mono-eyebrow">Response time</p>
              <p className="mt-1 text-white font-display text-xl">&lt; 4 hours</p>
            </div>
          </div>
        </div>

        {/* Massive wordmark — official brand colors: Blink (white) + Shots (red) with bloom */}
        <motion.div
          style={{ scale: wordmarkScale, rotateX: wordmarkRotateX }}
          className="mt-20 md:mt-28 text-center select-none"
          data-testid="footer-wordmark"
        >
          <div className="leading-none">
            <span className="block font-display font-bold tracking-tight text-[clamp(3.5rem,16vw,16rem)] leading-[0.85] whitespace-nowrap">
              <span
                className="text-white"
                style={{
                  textShadow:
                    "0 1px 0 rgba(0,0,0,0.5), 0 0 2px rgba(255,255,255,0.4)",
                }}
              >
                Blink
              </span>
              <span
                className="text-[#E10600]"
                style={{
                  textShadow:
                    "0 1px 0 rgba(0,0,0,0.5), 0 0 2px rgba(225,6,0,0.4)",
                }}
              >
                Shots
              </span>
            </span>
          </div>
        </motion.div>

        <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-3 border-t border-white/5 pt-6 text-xs font-mono text-neutral-500">
          <p data-testid="footer-copy">© 2025 BlinkShots Studio. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <span>Made with cinema lights</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
